import { useMemo, useState, type JSX, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router'
import { Check, GitMerge } from 'lucide-react'
import { useRecentConversations } from '../../../hooks/useRecentConversations.js'
import { useConversationActions } from '../../conversations/detail/useConversationActions.js'
import MergeDrawer, { type MergeCandidate, type MergeSubmit } from '../../conversations/detail/MergeDrawer.js'
import { R, StatusPill } from '../../conversations/detail/primitives.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { formatDateTime } from '../../../utils/timezone.js'

export interface ClientConversationsProps {
  clientId: string
}

export default function ClientConversations({ clientId }: ClientConversationsProps): JSX.Element {
  const [refreshKey, setRefreshKey] = useState(0)
  const { conversations, loading } = useRecentConversations(clientId, 50, 'client', refreshKey)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [mergeOpen, setMergeOpen] = useState(false)
  const navigate = useNavigate()
  const { mergeInto } = useConversationActions(null, () => { setRefreshKey((v) => v + 1) })

  const selectedConversations = useMemo(
    () => conversations.filter((c) => selectedIds.has(c.id)),
    [conversations, selectedIds],
  )

  const mergeCandidates: MergeCandidate[] = useMemo(
    () =>
      selectedConversations.map((c) => ({
        id: c.id,
        referenceId: c.referenceId,
        subject: c.subject ?? null,
        status: c.status ?? null,
        requester: c.displayName || c.primaryHandle || null,
        parentReferenceId: c.parentReferenceId ?? null,
        childCount: c.childCount ?? 0,
        mergedIntoReferenceId: c.mergedIntoReferenceId ?? null,
        secondaryCount: c.secondaryCount ?? 0,
        linkedRelationCount: c.linkedRelationCount ?? 0,
        approvalStatus: c.approvalStatus ?? null,
        approvalStateId: c.approvalStateId ?? null,
      })),
    [selectedConversations],
  )

  const defaultPrimaryId = mergeCandidates[0]?.id ?? ''

  const handleMergeConfirm = async (input: MergeSubmit) => {
    return mergeInto(input.primaryId, input.secondaryIds, {
      note: input.note,
      noteIsPublic: input.noteIsPublic,
      addSecondaryRecipientsToCc: input.addSecondaryRecipientsToCc,
      allowDifferentRequesters: input.allowDifferentRequesters,
    })
  }

  const toggleOne = (id: string, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const handleRowCheck = (id: string) => (e: ChangeEvent<HTMLInputElement>): void => {
    toggleOne(id, e.target.checked)
  }

  return (
    <div className="space-y-4">
      <div className={`overflow-hidden ${R} border border-slate-200 bg-white`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Client conversations</span>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => setMergeOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-[3px] bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] active:scale-[0.98]"
            >
              <GitMerge className="h-3.5 w-3.5" />
              Merge {selectedIds.size} conversation{selectedIds.size === 1 ? '' : 's'}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-xs font-semibold text-slate-500">
                <th className="w-10 px-4 py-2.5"></th>
                <th className="px-4 py-2.5">Reference</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Channel</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && conversations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                    No conversations for this client.
                  </td>
                </tr>
              )}
              {!loading &&
                conversations.map((c) => {
                  const ch = getChannelConfig(c.channelOrigin)
                  const isSelected = selectedIds.has(c.id)
                  return (
                    <tr
                      key={c.id}
                      className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50"
                      onClick={() => navigate(`/agent/conversation/view/${c.id}`)}
                    >
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <label
                          className={`relative flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-[3px] border-2 transition-all ${
                            isSelected
                              ? 'border-[#3366FF] bg-[#3366FF]'
                              : 'border-slate-300 bg-white hover:border-[#3366FF]/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={handleRowCheck(c.id)}
                            className="absolute h-full w-full cursor-pointer opacity-0"
                            aria-label={`Select conversation ${c.referenceId} for merge`}
                          />
                          {isSelected && <Check className="pointer-events-none h-3 w-3 text-white" strokeWidth={3} />}
                        </label>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-medium text-[#3366FF]">#{c.referenceId}</td>
                      <td className="max-w-[320px] truncate px-4 py-2.5 text-slate-700">
                        {c.subject || c.lastMessagePreview || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-xs ${ch.color}`}>
                          <ch.icon className="h-3 w-3" />
                          {ch.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">
                        {formatDateTime(c.lastMessageAt) || '—'}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      <MergeDrawer
        open={mergeOpen}
        onClose={() => {
          setMergeOpen(false)
          setSelectedIds(new Set())
        }}
        candidates={mergeCandidates}
        defaultPrimaryId={defaultPrimaryId}
        onConfirm={handleMergeConfirm}
      />
    </div>
  )
}
